import {
  Grid2X2Check,
  FolderKanbanIcon,
  BinocularsIcon,
  RefreshCwIcon,
  DiamondPlusIcon,
} from "lucide-react";
import { SidebarItemsType } from "../../types/sidebar";
import { IntegratedSystem } from "../../../../api/integratedsystems";
import { SyncIntegratedSystem } from "../../../../api/sync";
import { StandaloneSyncSystem } from "../../../../api/datasystems";

type EnrichSidebarSystem = Pick<StandaloneSyncSystem, "id" | "name">;

const SYNC_LINK_BUILDERS: Partial<
  Record<
    SyncIntegratedSystem["sync_data_type"],
    (sg: SyncIntegratedSystem) => string
  >
> = {
  CRM: (sg) =>
    `/product/sync/linkage/${sg.system_a_id}/${sg.sync_group_id}/contacts`,
  EVENTS: (sg) => `/product/sync/events/${sg.sync_group_id}`,
  CONTENT: (sg) => `/product/content/${sg.sync_group_id}`,
  MAILBOX: (sg) => `/product/mailbox/${sg.sync_group_id}`,
  ERM_PLATFORM: (sg) => `/product/erm-platform/${sg.sync_group_id}`,
};

/**
 * Shared function to create sidebar sections with configurable base path and options
 * This reduces duplication between integrated system and data project sidebars
 */
const createSharedSections = (
  selectedSystemId: number,
  config: {
    basePath: string;
  },
  syncGroups: SyncIntegratedSystem[] = [],
  enrichSystems: EnrichSidebarSystem[] = []
): SidebarItemsType[] => {
  const { basePath } = config;

  const otherSystemName = (sg: SyncIntegratedSystem) =>
    sg.system_a_id === selectedSystemId ? sg.system_b_name : sg.system_a_name;

  const getSyncLink = (syncGroup: SyncIntegratedSystem) =>
    SYNC_LINK_BUILDERS[syncGroup.sync_data_type]?.(syncGroup) ??
    `/app/sync/${selectedSystemId ?? ""}`;

  const getDataProjectLink = (type?: string) => {
    const baseUrl = "/product/data-projects";
    const systemId = selectedSystemId ?? "";

    if (type && type !== "data_quality") {
      return `${baseUrl}/${type}/${systemId}`;
    }
    return `${baseUrl}/${systemId}`;
  };

  const getEnrichFallbackLink = () =>
    `/product/enrich/${selectedSystemId ?? ""}`;

  const getEnrichProjectLink = (enrichSystemId?: number) => {
    if (!enrichSystemId) {
      return getDataProjectLink("enrich");
    }
    return `/product/data-projects/enrich/${
      selectedSystemId ?? ""
    }/${enrichSystemId}`;
  };

  const sections: SidebarItemsType[] = [
    {
      href: "/pages",
      icon: Grid2X2Check,
      title: "Data Quality",
      collapsedHref: `/product/dashboard/${selectedSystemId ?? ""}`,
      children: [
        {
          href: `${basePath}/dashboard/${selectedSystemId ?? ""}`,
          title: "DQ Data",
        },

        {
          href: `/product/data-projects/${selectedSystemId ?? ""}`,
          title: "Data Projects",
        },
      ],
    },
    {
      href: `${basePath}/contact-monitor`,
      icon: BinocularsIcon,
      title: "Monitor",
      collapsedHref: `/product/contact-monitor/${selectedSystemId ?? ""}`,
      children: [
        {
          href: `${basePath}/contact-monitor/${selectedSystemId ?? ""}`,
          title: "Monitor Data",
        },

        {
          href: getDataProjectLink("monitor"),
          title: "Monitor Projects",
        },
      ],
    },
  ];

  sections.push({
    href: "/product/sync",
    icon: RefreshCwIcon,
    title: "Sync",
    collapsedHref:
      syncGroups.length > 0
        ? getSyncLink(syncGroups[0])
        : `/product/sync-integrations`,
    children: syncGroups.map((syncGroup) => ({
      href: getSyncLink(syncGroup),
      title: otherSystemName(syncGroup),
    })),
  });

  const enrichChildren = enrichSystems.map((enrichSystem) => ({
    href: getEnrichProjectLink(enrichSystem.id),
    title: enrichSystem.name,
  }));

  sections.push(
    enrichChildren.length > 0
      ? {
          href: "/product/enrich",
          icon: DiamondPlusIcon,
          title: "Enrich",
          collapsedHref: enrichChildren[0].href,
          children: enrichChildren,
        }
      : {
          href: getEnrichFallbackLink(),
          icon: DiamondPlusIcon,
          title: "Enrich",
          collapsedHref: getEnrichFallbackLink(),
        }
  );

  sections.push({
    href: "/products",
    icon: FolderKanbanIcon,
    title: "Data Management",
    hideWhenCollapsed: true,
    children: [
      {
        href: `/product/data-projects/${selectedSystemId ?? ""}`,
        title: "Data Projects",
      },
      {
        href: "/pages/blank",
        title: "Alerts",
      },
      {
        href: "/product/rules",
        title: "Rules",
      },
      {
        href: `${basePath}/changed-fields/${selectedSystemId ?? ""}`,
        title: "Changed Fields",
      },
      {
        href: `${basePath}/rejected-fields/${selectedSystemId ?? ""}`,
        title: "Rejected Fields",
      },
      {
        href: `${basePath}/insights/${selectedSystemId ?? ""}`,
        title: "Insights",
      },
    ],
  });

  return sections;
};

const staticSections = (
  selectedSystemId: number,
  syncGroups: SyncIntegratedSystem[] = [],
  enrichSystems: EnrichSidebarSystem[] = []
) =>
  createSharedSections(
    selectedSystemId,
    {
      basePath: "/product",
    },
    syncGroups,
    enrichSystems
  );

export const createCirromSidebarItems = (
  _integratedSystems: IntegratedSystem[] = [],
  selectedSystemId: number,
  syncGroups: SyncIntegratedSystem[] = [],
  enrichSystems: EnrichSidebarSystem[] = []
) => [
  {
    title: "Pages",
    pages: [
      ...staticSections(selectedSystemId, syncGroups, enrichSystems),
    ] as SidebarItemsType[],
  },
];

// Data Project Sidebar - uses dataProjectId for data-project routes
// and integratedSystemId for product-level routes
const createDataProjectSections = (
  dataProjectId: number,
  integratedSystemId: number,
  enrichSystems: EnrichSidebarSystem[] = []
): SidebarItemsType[] => {
  const basePath = "/data-project";

  const getDataProjectLink = (type?: string) => {
    const baseUrl = "/product/data-projects";
    const systemId = integratedSystemId ?? "";
    if (type && type !== "data_quality") {
      return `${baseUrl}/${type}/${systemId}`;
    }
    return `${baseUrl}/${systemId}`;
  };

  const getEnrichFallbackLink = () =>
    `/product/enrich/${integratedSystemId ?? ""}`;

  const getEnrichProjectLink = (enrichSystemId?: number) => {
    if (!enrichSystemId) {
      return getDataProjectLink("enrich");
    }
    return `/product/data-projects/enrich/${
      integratedSystemId ?? ""
    }/${enrichSystemId}`;
  };

  const sections: SidebarItemsType[] = [
    {
      href: "/pages",
      icon: Grid2X2Check,
      title: "Data Quality",
      collapsedHref: `/data-project/dashboard/${dataProjectId ?? ""}`,
      children: [
        {
          href: `${basePath}/dashboard/${dataProjectId ?? ""}`,
          title: "DQ Data",
        },
        {
          href: `/product/data-projects/${integratedSystemId ?? ""}`,
          title: "Data Projects",
        },
      ],
    },
    {
      href: `${basePath}/contact-monitor`,
      icon: BinocularsIcon,
      title: "Monitor",
      collapsedHref: `/data-project/contact-monitor/${dataProjectId ?? ""}`,
      children: [
        {
          href: `${basePath}/contact-monitor/${dataProjectId ?? ""}`,
          title: "Monitor Data",
        },
        {
          href: getDataProjectLink("monitor"),
          title: "Monitor Projects",
        },
      ],
    },
  ];

  sections.push({
    href: "/product/sync",
    icon: RefreshCwIcon,
    title: "Sync",
    collapsedHref: `/product/sync/${integratedSystemId ?? ""}`,
    children: [
      {
        href: `/product/sync/${integratedSystemId ?? ""}`,
        title: "All Data",
      },
      {
        href: getDataProjectLink("sync"),
        title: "Data Projects",
      },
    ],
  });

  const enrichChildren = enrichSystems.map((enrichSystem) => ({
    href: getEnrichProjectLink(enrichSystem.id),
    title: enrichSystem.name,
  }));

  sections.push(
    enrichChildren.length > 0
      ? {
          href: "/product/enrich",
          icon: DiamondPlusIcon,
          title: "Enrich",
          collapsedHref: enrichChildren[0].href,
          children: enrichChildren,
        }
      : {
          href: getEnrichFallbackLink(),
          icon: DiamondPlusIcon,
          title: "Enrich",
          collapsedHref: getEnrichFallbackLink(),
        }
  );

  sections.push({
    href: "/products",
    icon: FolderKanbanIcon,
    title: "Data Management",
    hideWhenCollapsed: true,
    children: [
      {
        href: `/product/data-projects/${integratedSystemId ?? ""}`,
        title: "Data Projects",
      },
      {
        href: "/pages/blank",
        title: "Alerts",
      },
      {
        href: "/product/rules",
        title: "Rules",
      },
      {
        href: `/product/changed-fields/${integratedSystemId ?? ""}`,
        title: "Changed Fields",
      },
      {
        href: `/product/rejected-fields/${integratedSystemId ?? ""}`,
        title: "Rejected Fields",
      },
      {
        href: `/product/insights/${integratedSystemId ?? ""}`,
        title: "Insights",
      },
    ],
  });

  return sections;
};

export const createDataProjectSidebarItems = (
  _integratedSystems: IntegratedSystem[] = [],
  dataProjectId: number,
  integratedSystemId?: number,
  enrichSystems: EnrichSidebarSystem[] = []
) => [
  {
    title: "Pages",
    pages: [
      ...createDataProjectSections(
        dataProjectId,
        integratedSystemId ?? dataProjectId,
        enrichSystems
      ),
    ] as SidebarItemsType[],
  },
];
